/**
 * Camera device action preparations (cloud Scenes).
 *
 * Camera actions are written to the write-only IoTS tokens `camera_action` /
 * `camerazone_action` / `playsound` / `cam_arm{n}` / `cam_privacy{n}`. The UI-action ->
 * token/set mapping lives in `camera_action_helpers.ts`; this preparer only wraps it
 * into the scene payload.
 */

import type {
	DeviceActionContext,
	PreparedDeviceAction,
} from 'src/components/Scenes/helpers/device_actions/types';
import { buildCameraActionSet } from 'src/components/Scenes/helpers/camera_action_helpers';
import { getCameraZoneName } from 'src/components/Scenes/SceneWizard/Process/When/AddEditContionWizard/Steps/DeviceBased/helpers/camera-scene-helpers';
import {
	getCameraZone,
	getCameraZonesList,
	getZoneId,
} from 'src/components/Camera/helpers/camera-zone-helpers';
import type { CameraZoneType } from 'src/components/Camera/types/camera-zones';
import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';

export type CameraActionZone = {
	id: string;
	title: string;
	type: CameraZoneType;
	icon: string;
};

/**
 * Icon mirroring the camera zones drawer (ZonesList.svelte): a privacy zone uses
 * the "video off" glyph, a motion/detection zone uses the bounding-box glyph.
 */
const getCameraZoneTypeIcon = (type: CameraZoneType): string =>
	type === 'privacy' ? 'fas fa-video-slash' : 'fas fa-vector-square';

/**
 * Build the zone list for the camera action wizard. Unlike the conditions
 * picker ({@link getCameraMotionZones}), actions can enable/disable BOTH motion
 * and privacy zones, so no zone-type filtering is applied here.
 *
 * Mirrors the conditions helper signature (deviceId + settings map) so the
 * component reads zones the same way on both the When and Do sides.
 */
export const getCameraActionZones = (
	deviceId: string | undefined,
	deviceSettings: Record<string, DeviceSettingsG2 | undefined>,
): CameraActionZone[] => {
	if (!deviceId) {
		return [];
	}

	const settings = deviceSettings[deviceId.split('_')[0]];
	if (!settings) {
		return [];
	}

	return getCameraZonesList(settings)
		.map((componentKey: string) => {
			const zoneId = getZoneId(componentKey);
			const type: CameraZoneType = getCameraZone(settings, zoneId)?.type ?? 'motion';
			return {
				id: String(zoneId),
				title: getCameraZoneName(zoneId, settings),
				type,
				icon: getCameraZoneTypeIcon(type),
			};
		})
		.sort((a, b) => Number(a.id) - Number(b.id));
};

/**
 * Prepares action payload for camera devices.
 *
 * @param context - Context containing shared helpers, channel metadata, and queued device actions.
 * @returns Prepared `{ r, set }` payload, or `undefined` when the action is unsupported.
 */
export const prepareCameraAction = (context: DeviceActionContext): PreparedDeviceAction | undefined => {
	const { hexToDecimal, channel, actions } = context;
	const action = actions[0] as
		| { name: string; value: unknown; add_info_ind?: string; addOnInd?: number; duration?: number }
		| undefined;

	if (!action) {
		return undefined;
	}

	const zoneId = action.add_info_ind ?? action.addOnInd;

	const payload = buildCameraActionSet(action.name, action.value, {
		channel,
		zoneId,
		duration: action.duration,
	});

	if (!payload) {
		return undefined;
	}

	return {
		_gui_purpose: 'action',
		r: 'dev:' + hexToDecimal + ':' + payload.token,
		set: payload.set,
	};
};
